// Constants and types for client/server contracts re: product analytics tracking

import {
  Array,
  Boolean,
  Literal,
  Null,
  Number,
  Record as RRecord,
  Runtype,
  Static,
  String,
  Union,
} from "runtypes";
import { z } from "zod";

import {
  DataConnectionTypeLiteral,
  SqlCompletionTypeLiteral,
} from "./dataConnectionTypes.js";
import {
  CellType,
  DataBrowserTabLiteral,
  NotificationTypeLiteral,
  OrgRole,
  OrgRoleLiteral,
  ProjectRoleLiteral,
  ViewDataOpenedFromLiteral,
} from "./enums.js";
import { AppFeatureFlagRuntype } from "./FeatureFlag.js";
import { FeatureGateClickedEventData } from "./FeatureGate.js";
import {
  AgentChatThreadId,
  CellId,
  CollectionId,
  ContextSuggestionIssueId,
  DataConnectionId,
  DataSourceTableId,
  HexId,
  HexVersionId,
  MagicEventId,
  NotificationUserLinkId,
  SemanticDatasetId,
  SemanticProjectId,
  SemanticViewId,
  ThreadExtractionExperimentId,
} from "./idTypeBrands.js";
import { AgentChatThreadTypeLiteral } from "./magic/agent/threads.js";
import { ProjectTemplateLiteral } from "./projectTemplate.js";
import { getNormalEnum } from "./runtypeEnums.js";
import { NoCodeCellTypeLiteral } from "./sql/dataSourceTableConfig.js";
import { Requiredish } from "./utils/typeUtils.js";
import {
  ActionTakenLiteral,
  OnboardingFlowLiteral,
  OnboardingFlowStepLiteral,
} from "./workspaceSetupTypes.js";

/**
 * Amplitude event types that can be tracked via POST request to /track-event
 * Variable names should match the event_type defined in the Amplitude schema
 * The /track-event endpoint will reject any events not defined here
 * and explicitly handled in the server file
 */
export const ClientAnalyticsEventTypeLiteral = Union(
  Literal("FEATURE_GATE_CLICKED"),
  Literal("DATA_CONNECTION_CREATION_FAILED"),
  Literal("DATA_CONNECTION_FORM_OPENED"),
  Literal("AUTOCOMPLETE_ACCEPTED"),
  Literal("AUTOCOMPLETE_TRIGGERED"),
  Literal("PROJECT_EMPTY_STATE_CELL_CLICKED"),
  Literal("FEATURE_FLAG_EVALUATED"),
  Literal("ONBOARDING_STEP_ADVANCED"),
  Literal("VIEW_DATA_OPENED"),
  Literal("PROJECT_TEMPLATE_PREVIEWED"),
  Literal("DEBUG_MODE_OPENED"),
  Literal("NOTIFICATION_INBOX_OPENED"),
  Literal("NOTIFICATION_CLICKED"),
  Literal("PRESENTATION_MODE_OPENED"),
  Literal("KEYBOARD_SHORTCUT_USED"),
  Literal("DATA_BROWSER_DIALOG_OPENED"),
  Literal("DATA_BROWSER_TABLE_SELECTED"),
  Literal("PUBLISH_MODAL_OPENED"),
  Literal("CLIENT_COUNTER_EVENT"),
  Literal("PROJECT_LIST_LAYOUT_UPDATED"),
  Literal("PROJECT_LIST_SORT_UPDATED"),
  Literal("PROJECT_LIST_FILTER_UPDATED"),
  Literal("EXPLORE_CREATED"),
  Literal("EXPLORE_ERRORED"),
  Literal("SEMANTIC_DATASET_SELECTED"),
  Literal("CTA_CLICKED"),
  Literal("FEATURE_GATE_CONTACT_SALES_SUBMITTED"),
  Literal("CTA_CONTACT_SALES_SUBMITTED"),
  Literal("CANCELLATION_STEP_ADVANCED"),
  Literal("SEMANTIC_WORKBENCH_PUBLISH_TAB_CLICKED"),
  Literal("DRILLED_INTO_EXPLORE"),
  Literal("SEMANTIC_VIEW_SELECTED"),
  Literal("THREAD_VIEWED"),
  Literal("THREAD_INSPECTOR_CLICK"),
  Literal("WORKSPACE_SEARCH_RESULT_CLICKED"),
  Literal("DATA_BROWSER_SEARCH_TRIGGERED"),
  Literal("DATA_BROWSER_SEARCH_RESULT_SELECTED"),
  Literal("CONTEXT_STUDIO_THREAD_OPENED"),
  Literal("CONTEXT_STUDIO_FILTER_CHANGED"),
  Literal("CONTEXT_STUDIO_VIEWED"),
  Literal("CONTEXT_STUDIO_SUGGESTION_UI_ACTION"),
  Literal("EXECUTION_PROFILER_OPENED"),
  Literal("EXECUTION_PROFILER_TRACE_ID_COPIED"),
  Literal("AGENT_MODEL_EFFORT_SELECTED"),
  Literal("USER_CONTEXT_EXHAUST_PROMPT_DECISION"),
  Literal("USER_CONTEXT_EXHAUST_PROMPT_VIEWED"),
);
export type ClientAnalyticsEventType = Static<
  typeof ClientAnalyticsEventTypeLiteral
>;
export const ClientAnalyticsEventType = getNormalEnum(
  ClientAnalyticsEventTypeLiteral,
);

const DataBrowserDialogOpenedSourceLiteral = Union(
  Literal("PROJECT_EMPTY_STATE"),
  Literal("EXPLORE_EMPTY_STATE"),
  Literal("EXPLORE_JOIN_DATA"),
  Literal("ACTION_BAR"),
  Literal("ADD_CELL_MENU"),
  Literal("SIDEBAR"),
  Literal("SIDEBAR_PREVIEW"),
  Literal("UNKNOWN"),
  Literal("HOME_DATA_TAB"),
  Literal("MAGIC_FEEDBACK"),
  Literal("MAGIC_MENTION"),
  Literal("LOGIC_VIEW_PROMPT_BAR"),
);

export const DataBrowserDialogOpenedSource = {
  ...getNormalEnum(DataBrowserDialogOpenedSourceLiteral),
  ...getNormalEnum(NoCodeCellTypeLiteral),
};
export type DataBrowserDialogOpenedSource = Static<
  typeof DataBrowserDialogOpenedSourceLiteral | typeof NoCodeCellTypeLiteral
>;

export const DataBrowserDialogOpenedEventData = RRecord({
  dataBrowserOpenedSource: DataBrowserDialogOpenedSourceLiteral.Or(
    NoCodeCellTypeLiteral,
  ),
});
type DataBrowserDialogOpenedEventData = Static<
  typeof DataBrowserDialogOpenedEventData
>;

export const DataBrowserTypeLiteral = Union(
  Literal("PROJECT_DIALOG"),
  Literal("PROJECT_SIDEBAR"),
  Literal("HOME_DATA_TAB"),
  Literal("HOME_DATA_BENTO"),
  Literal("HOME_SIDEBAR"),
  Literal("PROJECT_EMPTY_STATE"),
  Literal("EXPLORE_EMPTY_STATE"),
  Literal("THREAD_AGENT_TOOL_RESULT"),
);

export type DataBrowserType = Static<typeof DataBrowserTypeLiteral>;
export const DataBrowserType = getNormalEnum(DataBrowserTypeLiteral);

const TableActionTakenLiteral = NoCodeCellTypeLiteral.Or(
  Literal(CellType.WRITEBACK),
)

  .Or(Literal(CellType.SQL))

  .Or(Literal("SELECT"))
  .Or(Literal("EXPLORE_UI"));

export const DataBrowserTableSelectedEventData = RRecord({
  browserType: DataBrowserTypeLiteral,
  dataSourceTableId: DataSourceTableId.Or(Null),
  dataConnectionId: DataConnectionId.Or(Null),

  tableActionTaken: TableActionTakenLiteral,
  dataBrowserTab: DataBrowserTabLiteral,
});
export type DataBrowserTableSelectedEventData = Static<
  typeof DataBrowserTableSelectedEventData
>;

export const SemanticDatasetSelectedEventData = RRecord({
  tableActionTaken: TableActionTakenLiteral,
  dataBrowserTab: DataBrowserTabLiteral,
  browserType: DataBrowserTypeLiteral,
  semanticDatasetId: SemanticDatasetId,
  semanticProjectId: SemanticProjectId,
});

export type SemanticDatasetSelectedEventData = Static<
  typeof SemanticDatasetSelectedEventData
>;

export const SemanticViewSelectedEventData = RRecord({
  tableActionTaken: TableActionTakenLiteral,
  dataBrowserTab: DataBrowserTabLiteral,
  browserType: DataBrowserTypeLiteral,
  semanticViewId: SemanticViewId,
  semanticProjectId: SemanticProjectId,
});

export type SemanticViewSelectedEventData = Static<
  typeof SemanticViewSelectedEventData
>;

export const ThreadViewedEventData = RRecord({
  threadId: AgentChatThreadId,
  threadType: AgentChatThreadTypeLiteral,
});
export type ThreadViewedEventData = Static<typeof ThreadViewedEventData>;

export const ThreadInspectorClickItemClickedLiteral = Union(
  Literal("OPEN"),
  Literal("CLOSE"),
  Literal("TIMELINE_TAB"),
  Literal("WARNINGS_TAB"),
  Literal("CONTEXT_STUDIO_LINK"),
);
export type ThreadInspectorClickItemClicked = Static<
  typeof ThreadInspectorClickItemClickedLiteral
>;
export const ThreadInspectorClickItemClicked = getNormalEnum(
  ThreadInspectorClickItemClickedLiteral,
);

export const ThreadInspectorClickEventData = RRecord({
  itemClicked: ThreadInspectorClickItemClickedLiteral,
  threadId: AgentChatThreadId.optional(),
});
export type ThreadInspectorClickEventData = Static<
  typeof ThreadInspectorClickEventData
>;

const ProjectEmptyStateCellClickedTriggeredFromLiteral = Union(
  Literal("FEATURED"),
  Literal("ADD_CELL_MENU"),
  Literal("WORKSPACE_DATA_CONNECTION"),
  Literal("FILE_UPLOAD"),
);

export const ProjectEmptyStateCellClickedTriggeredFrom = getNormalEnum(
  ProjectEmptyStateCellClickedTriggeredFromLiteral,
);

export type ProjectEmptyStateCellClickedTriggeredFrom = Static<
  typeof ProjectEmptyStateCellClickedTriggeredFromLiteral
>;
export const ProjectEmptyStateCellClickedEventData = RRecord({
  cellType: String,
  projectId: HexId,
  triggeredFrom: ProjectEmptyStateCellClickedTriggeredFromLiteral,
  orgRole: OrgRoleLiteral.optional(),
});

export type ProjectEmptyStateCellClickedEventData = Static<
  typeof ProjectEmptyStateCellClickedEventData
>;

// used for the DataConnectionCreationFailed event and the DataConnectionFormOpened event
export const DataConnectionCreationEventData = RRecord({
  connectionType: DataConnectionTypeLiteral,
  projectVersionId: HexVersionId.optional(),
  orgRole: OrgRoleLiteral.optional(),
});
type DataConnectionCreationEventData = Static<
  typeof DataConnectionCreationEventData
>;

export const DataBrowserSearchTriggeredEventData = RRecord({});
export type DataBrowserSearchTriggeredEventData = Static<
  typeof DataBrowserSearchTriggeredEventData
>;
export const DataBrowserSearchResultSelectedEventData = RRecord({
  resultType: String,
  networkRank: Number.Or(Null).optional(),
  uiRank: Number.Or(Null).optional(),
});
export type DataBrowserSearchResultSelectedEventData = Static<
  typeof DataBrowserSearchResultSelectedEventData
>;

export const WorkspaceSearchResultTypeLiteral = Union(
  Literal("LOGIC"),
  Literal("APP"),
  Literal("LOGIC-CELL"),
);
export type WorkspaceSearchResultType = Static<
  typeof WorkspaceSearchResultTypeLiteral
>;
export const WorkspaceSearchResultType = getNormalEnum(
  WorkspaceSearchResultTypeLiteral,
);

export const WorkspaceSearchResultClickedEventData = RRecord({
  rank: Number,
  searchTraceId: String.optional(),
  resultType: WorkspaceSearchResultTypeLiteral,
  projectId: HexId.optional(),
});
type WorkspaceSearchResultClickedEventData = Static<
  typeof WorkspaceSearchResultClickedEventData
>;
export const SqlAutocompleteAcceptedEventData = RRecord({
  autocompleteProviderType: Literal("SQL"),
  networkRank: Number.optional(),
  suggestionType: SqlCompletionTypeLiteral,
});
type SqlAutocompleteAcceptedEventData = Static<
  typeof SqlAutocompleteAcceptedEventData
>;
export const SqlAutocompleteTriggeredEventData = RRecord({
  autocompleteProviderType: Literal("SQL"),
});
type SqlAutocompleteTriggeredEventData = Static<
  typeof SqlAutocompleteTriggeredEventData
>;

export const KeyboardShortcutUsedEventData = RRecord({
  key: String,
  action: String,
});
type KeyboardShortcutUsedEventData = Static<
  typeof KeyboardShortcutUsedEventData
>;

export const FlagEvaluationTypeLiteral = Union(
  Literal("ENV"),
  Literal("ORG"),
  Literal("USER"),
);

export const FlagEvaluationType = getNormalEnum(FlagEvaluationTypeLiteral);

export const FeatureFlagEvaluatedEventData = RRecord({
  orgRole: OrgRoleLiteral.optional(),
  variation: String,
  featureFlagName: AppFeatureFlagRuntype,
});
type FeatureFlagEvaluatedEventData = Static<
  typeof FeatureFlagEvaluatedEventData
>;

export const NotificationInboxOpenedEventData = RRecord({
  numberUnreadNotifications: Number,
});
type NotificationInboxOpenedEventData = Static<
  typeof NotificationInboxOpenedEventData
>;

export const NotificationClickedEventData = RRecord({
  notificationId: NotificationUserLinkId,
  notificationType: NotificationTypeLiteral,
});
type NotificationClickedEventData = Static<typeof NotificationClickedEventData>;

export const PublishModalOpenedEventData = RRecord({
  projectId: HexId,
  projectVersionId: HexVersionId,
  projectRole: ProjectRoleLiteral.optional(),
});
type PublishModalOpenedEventData = Static<typeof PublishModalOpenedEventData>;

/**
 * Constants and utils for identifying anonymous users in 3rd party
 * analytic services such as Rudderstack and LaunchDarkly.
 * These services typically charge by MAU, where each anonymous user
 * would count as a new MAU - so we want to treat anonymous users for those
 * services as a single user.
 * See https://docs.launchdarkly.com/home/users/anonymous-users
 */
export const ANALYTICS_ANONYMOUS_USER = {
  id: "bb92b229-9d51-491d-a679-9a0734f1e21c",
  email: "anonymous@example.com",
  name: "anonymous",
  stackId: "anonymous",
  orgRole: OrgRole.ANONYMOUS,
};

export const TriggeredFromLiteral = Union(
  Literal("WORKSPACE_SETUP"),
  Literal("SETUP_CHECKLIST"),
  Literal("WORKSPACE_SETTINGS"),
  Literal("CONTEXT_SETUP"),
  Literal("AGGREGATE_BILLABLE_USAGE_JOB"),
);

export type TriggeredFromType = Static<typeof TriggeredFromLiteral>;

export const TriggeredFromType = getNormalEnum(TriggeredFromLiteral);

export const PublishedAppViewInAppReferrerLiteral = Union(
  Literal("WORKSPACE_SEARCH"),
  Literal("RECENTLY_VIEWED_BENTO"),
  Literal("POPULAR_THIS_WEEK_BENTO"),
  Literal("RECENTLY_PUBLISHED_BENTO"),
  Literal("ALL_PROJECTS"),
  Literal("FAVORITES"),
  Literal("COLLECTION"),
  Literal("DEFLECTION"),
  Literal("SEMANTIC_PROJECT_WORKBENCH"),
);
export type PublishedAppViewInAppReferrer = Static<
  typeof PublishedAppViewInAppReferrerLiteral
>;
export const PublishedAppViewInAppReferrer = getNormalEnum(
  PublishedAppViewInAppReferrerLiteral,
);

const ProjectFileUploadTriggeredFromLiteral = Union(
  Literal("PROJECT_EMPTY_STATE"),
  Literal("PROJECT_SIDEBAR"),
  Literal("CELL_EMBED"),
  Literal("PROJECT_FILE_UPLOAD_CELL"),
  Literal("PROJECT_ONBOARDING"),
  Literal("AI_PROMPT_ONBOARDING"),
  Literal("AI_PROMPT_ONBOARDING_DEMO_CSV"),
  Literal("NOTEBOOK_AGENT_PROMPT_BAR"),
  Literal("THREAD_PROMPT_BAR"),
  Literal("QUICK_SEARCH"),
  Literal("THREAD_PREVIEW"),
);

export const ProjectTemplatePreviewedEventData = RRecord({
  projectHasCells: Boolean,
  templateName: ProjectTemplateLiteral,
  orgRole: OrgRoleLiteral.optional(),
});
export type ProjectTemplatePreviewedEventData = Static<
  typeof ProjectTemplatePreviewedEventData
>;

export type ProjectFileUploadTriggeredFrom = Static<
  typeof ProjectFileUploadTriggeredFromLiteral
>;
export const ProjectFileUploadTriggeredFrom = getNormalEnum(
  ProjectFileUploadTriggeredFromLiteral,
);

export const OnboardingStepAdvancedEventData = RRecord({
  actionTaken: ActionTakenLiteral,
  flow: OnboardingFlowLiteral,
  step: OnboardingFlowStepLiteral,
  orgRole: OrgRoleLiteral.optional(),
  properties: RRecord({
    demoPrompt: String.optional(),
    dataConnectionType: DataConnectionTypeLiteral.optional(),
    uploadedFiles: Number.optional(),
    onlyHasDemoConnection: Boolean.optional(),
  }),
});
export type OnboardingStepAdvancedEventData = Static<
  typeof OnboardingStepAdvancedEventData
>;

export const ViewDataOpenedEventData = RRecord({
  projectId: HexId,
  cellType: String,
  triggeredFrom: ViewDataOpenedFromLiteral,
});
export type ViewDataOpenedEventData = Static<typeof ViewDataOpenedEventData>;

export const DebugModeOpenedEventData = RRecord({
  projectId: HexId,
});
export type DebugModeOpenedEventData = Static<typeof DebugModeOpenedEventData>;

export const PresentationModeOpenedEventData = RRecord({
  projectId: HexId,
});
export type PresentationModeOpenedEventData = Static<
  typeof PresentationModeOpenedEventData
>;

export const ClientCounterEventData = RRecord({
  eventType: String,
});
export type ClientCounterEventData = Static<typeof ClientCounterEventData>;

export const ProjectsPage = Union(
  Literal("PROJECTS"),
  Literal("COLLECTION"),
  Literal("COMPONENTS"),
  Literal("ARCHIVE"),
  Literal("TRASH"),
  Literal("LIBRARY"),
  Literal("EXPLORATIONS"),
  Literal("SEMANTIC_PROJECT_WORKBENCH"),
  Literal("ENDORSEMENTS"),
);
export type ProjectsPage = Static<typeof ProjectsPage>;

export const ProjectListLayoutUpdatedEventData = RRecord({
  layout: Union(Literal("CARD"), Literal("LIST")),
  collectionId: CollectionId.optional(),
  page: ProjectsPage,
  orgRole: OrgRoleLiteral.optional(),
});
export type ProjectListLayoutUpdatedEventData = Static<
  typeof ProjectListLayoutUpdatedEventData
>;

export const ProjectListSortUpdatedEventData = RRecord({
  layout: Union(Literal("CARD"), Literal("LIST")),
  direction: Union(Literal("ASC"), Literal("DESC")).optional(),
  sortField: String.optional(),
  triggeredFrom: Union(Literal("TABLE_HEADER"), Literal("SORT_DROPDOWN")),
  orgRole: OrgRoleLiteral.optional(),
  updateType: Union(Literal("SORT_FIELD"), Literal("DIRECTION")),
});
export type ProjectListSortUpdatedEventData = Static<
  typeof ProjectListSortUpdatedEventData
>;

export const ProjectListFilterUpdatedEventData = RRecord({
  layout: Union(Literal("CARD"), Literal("LIST")),
  orgRole: OrgRoleLiteral.optional(),
  /**
   * The filter type that is being updated, e.g. "owner", "creator", "workspace", "public", "my-access".
   */
  updateType: String,
  /**
   * The filter value for the updated filter type.
   */
  updateValue: Union(String, Boolean, Null),
  /**
   * All active filter types at the time that updateType was updated.
   */
  activeFilters: Array(String),
});
export type ProjectListFilterUpdatedEventData = Static<
  typeof ProjectListFilterUpdatedEventData
>;

export const ExploreCreatedEventData = RRecord({
  projectId: HexId,
  projectVersionId: HexVersionId,
  magicEventId: MagicEventId,
  // just being used for magic for now but can be extended in the future
  exploreSource: Union(
    Literal("ASK_MAGIC_HOMEPAGE_USER_PROMPT"),
    Literal("ASK_MAGIC_HOMEPAGE_SUGGESTED_PROMPT"),
    Literal("ASK_MAGIC_EXPLORE_EMPTY_STATE_USER_PROMPT"),
    Literal("ASK_MAGIC_EXPLORE_EMPTY_STATE_SUGGESTED_PROMPT"),
    Literal("ASK_MAGIC_EXPLORE_TABLE_SELECTED_USER_PROMPT"),
    Literal("ASK_MAGIC_EXPLORE_TABLE_SELECTED_SUGGESTED_PROMPT"),
  ),
  exploreDatasourceType: Union(
    Literal("DATAFRAME"),
    Literal("SEMANTIC"),
    Literal("DATAWAREHOUSE"),
  ),
  hydrated: Boolean.optional(),
});

export type ExploreCreatedEventData = Static<typeof ExploreCreatedEventData>;

export const ExploreErroredEventData = RRecord({
  hasMagicEvent: Boolean,
  inputType: Union(
    Literal("TABLE"),
    Literal("DATAFRAME"),
    Literal("SEMANTIC_DATASET"),
  ),
  recognizedErrorType: String.optional(),
  orgRole: OrgRoleLiteral.optional(),
  outputId: String,
  cellId: CellId,
});
export type ExploreErroredEventData = Static<typeof ExploreErroredEventData>;

export const CtaClickedEventData = RRecord({
  cta: Union(
    Literal("CONTACT_SALES"),
    Literal("VIEW_PLANS"),
    Literal("CHOOSE_PROFESSIONAL"),
    Literal("CHOOSE_TEAM"),
    Literal("CHOOSE_COMMUNITY"),
    Literal("CHECKOUT_TEAM"),
    Literal("VIEW_BILLING"),
  ),
  triggeredFrom: Union(
    Literal("TRIAL_BANNER"),
    Literal("PRICING_PAGE"),
    Literal("COMMUNITY_UPGRADE_BANNER"),
    Literal("AI_PROMPT_LIMIT_CONTAINER"),
    Literal("TRIAL_END_MODAL"),
    Literal("AI_CREDIT_LIMIT_BANNER"),
  ),
});
export type CtaClickedEventData = Static<typeof CtaClickedEventData>;

export const SemanticWorkbenchPublishTabClickedEventData = RRecord({
  semanticProjectId: SemanticProjectId,
  semanticWorkbenchPublishTab: Union(
    Literal("CHANGES"),
    Literal("REFERENCES"),
    Literal("PREVIEW"),
    Literal("ASK_PREVIEW"),
  ),
});
export type SemanticWorkbenchPublishTabClickedEventData = Static<
  typeof SemanticWorkbenchPublishTabClickedEventData
>;

export const DrilledIntoExploreEventData = RRecord({
  drill_context: Union(
    Literal("APP"),
    Literal("EXPLORATION"),
    Literal("EXPLORE_CELL"),
  ),
  drill_entry_point: Union(
    Literal("CHART"),
    Literal("PIVOT"),
    Literal("NESTED_TABLE"),
  ),
  drill_field_type: Union(
    Literal("STRING"),
    Literal("NUMERIC"),
    Literal("BOOLEAN"),
    Literal("DATETIME"),
  ),
});
export type DrilledIntoExploreEventData = Static<
  typeof DrilledIntoExploreEventData
>;

export const FeatureGateContactSalesSubmittedEventData = RRecord({
  campaign: String,
  featureGateDescription: String,
  orgRole: OrgRoleLiteral.optional(),
  message: String.optional(),
});
export type FeatureGateContactSalesSubmittedEventData = Static<
  typeof FeatureGateContactSalesSubmittedEventData
>;

export const CtaContactSalesSubmittedEventData = RRecord({
  campaign: String,
  orgRole: OrgRoleLiteral.optional(),
  message: String.optional(),
});
export type CtaContactSalesSubmittedEventData = Static<
  typeof CtaContactSalesSubmittedEventData
>;

export const CancellationStepAdvancedEventData = RRecord({
  cancellationFlow: String,
  cancellationStep: String,
  actionTaken: String,
  cancellationReason: String.optional(),
  cancellationReasonOtherDetail: String.optional(),
});
export type CancellationStepAdvancedEventData = Static<
  typeof CancellationStepAdvancedEventData
>;

export const ContextStudioThreadOpenedEventData = RRecord({
  threadId: AgentChatThreadId,
  threadType: AgentChatThreadTypeLiteral,
  threadOpenedFrom: Union(Literal("MAIN_TABLE")),
});
export type ContextStudioThreadOpenedEventData = Static<
  typeof ContextStudioThreadOpenedEventData
>;

export const ContextStudioFilterChangedEventData = RRecord({
  filterType: Union(
    Literal("AGENT_TYPE"),
    Literal("ORG_ROLE"),
    Literal("USER"),
    Literal("TIME_PERIOD"),
    Literal("AGENT_CHAT_MESSAGE_SOURCE"),
    Literal("FEEDBACK"),
    Literal("WARNING_TYPE"),
    Literal("CLEAR_ALL"),
  ),
});
export type ContextStudioFilterChangedEventData = Static<
  typeof ContextStudioFilterChangedEventData
>;

const ContextStudioPageLiteral = Union(
  Literal("DASHBOARD"),
  Literal("CONTEXT_SOURCES"),
  Literal("GUIDES"),
  Literal("ENDORSEMENTS"),
  Literal("MODELS"),
  Literal("REPOSITORIES"),
  Literal("SETUP"),
  Literal("SUGGESTIONS"),
  Literal("SUGGESTION_SUMMARY"),
  Literal("SUGGESTION_CHANGES"),
  Literal("MODEL_REFERENCES"),
  Literal("APPS"),
);

export const ContextStudioViewedEventData = RRecord({
  contextStudioPage: ContextStudioPageLiteral,
});
export type ContextStudioViewedEventData = Static<
  typeof ContextStudioViewedEventData
>;

export const ContextStudioSuggestionUiActionTypeLiteral = Union(
  Literal("SUGGESTION_LIST_ITEM_CLICKED"),
  Literal("CLI_COMMAND_COPIED"),
  Literal("EVIDENCE_THREAD_LINK_CLICKED"),
  Literal("EVIDENCE_WARNING_EXPANDED"),
  Literal("THREAD_INSPECTOR_SUGGESTION_LINK_CLICKED"),
);
export type ContextStudioSuggestionUiActionType = Static<
  typeof ContextStudioSuggestionUiActionTypeLiteral
>;
export const ContextStudioSuggestionUiActionType = getNormalEnum(
  ContextStudioSuggestionUiActionTypeLiteral,
);

export const ContextStudioSuggestionUiActionEventData = RRecord({
  actionType: ContextStudioSuggestionUiActionTypeLiteral,
  suggestionId: ContextSuggestionIssueId.optional(),
  threadId: AgentChatThreadId.optional(),
  triggeredFrom: Union(
    Literal("HOME_PAGE"),
    Literal("SUGGESTIONS_PAGE"),
  ).optional(),
});
export type ContextStudioSuggestionUiActionEventData = Static<
  typeof ContextStudioSuggestionUiActionEventData
>;

const ExecutionProfilerTriggeredFromLiteral = Union(
  Literal("HELP_MENU"),
  Literal("CELL_METADATA"),
  Literal("PUBLISHED_APP"),
);

export const ExecutionProfilerOpenedEventData = RRecord({
  projectId: String.optional(),
  triggeredFrom: ExecutionProfilerTriggeredFromLiteral,
});
export type ExecutionProfilerOpenedEventData = Static<
  typeof ExecutionProfilerOpenedEventData
>;

export const ExecutionProfilerTraceIdCopiedEventData = RRecord({
  projectId: String.optional(),
});
export type ExecutionProfilerTraceIdCopiedEventData = Static<
  typeof ExecutionProfilerTraceIdCopiedEventData
>;

export const AgentModelEffortSelectedEventData = RRecord({
  selectionType: Union(Literal("MODEL"), Literal("EFFORT")),
  fromModel: String,
  toModel: String,
  fromEffort: String,
  toEffort: String,
  modelChanged: Boolean,
  effortChanged: Boolean,
});
export type AgentModelEffortSelectedEventData = Static<
  typeof AgentModelEffortSelectedEventData
>;

export const UserContextExhaustPromptDecisionActionLiteral = Union(
  Literal("ACCEPTED"),
  Literal("REJECTED"),
  Literal("DISMISSED"),
);
export type UserContextExhaustPromptDecisionAction = Static<
  typeof UserContextExhaustPromptDecisionActionLiteral
>;

export const UserContextExhaustPromptDecisionEventData = RRecord({
  userAction: UserContextExhaustPromptDecisionActionLiteral,
  threadId: AgentChatThreadId,
  threadExtractionExperimentId: ThreadExtractionExperimentId,
});
export type UserContextExhaustPromptDecisionEventData = Static<
  typeof UserContextExhaustPromptDecisionEventData
>;

export const UserContextExhaustPromptViewedEventData = RRecord({
  threadId: AgentChatThreadId,
  threadExtractionExperimentId: ThreadExtractionExperimentId,
});
export type UserContextExhaustPromptViewedEventData = Static<
  typeof UserContextExhaustPromptViewedEventData
>;

export type ClientAnalyticsEventDataType =
  | FeatureGateClickedEventData
  | DataConnectionCreationEventData
  | SqlAutocompleteAcceptedEventData
  | SqlAutocompleteTriggeredEventData
  | ProjectEmptyStateCellClickedEventData
  | FeatureFlagEvaluatedEventData
  | OnboardingStepAdvancedEventData
  | ViewDataOpenedEventData
  | ProjectTemplatePreviewedEventData
  | DebugModeOpenedEventData
  | NotificationInboxOpenedEventData
  | NotificationClickedEventData
  | KeyboardShortcutUsedEventData
  | PresentationModeOpenedEventData
  | DataBrowserDialogOpenedEventData
  | DataBrowserTableSelectedEventData
  | PublishModalOpenedEventData
  | ClientCounterEventData
  | ProjectListLayoutUpdatedEventData
  | ProjectListSortUpdatedEventData
  | ProjectListFilterUpdatedEventData
  | ExploreCreatedEventData
  | ExploreErroredEventData
  | SemanticDatasetSelectedEventData
  | CtaClickedEventData
  | CancellationStepAdvancedEventData
  | SemanticViewSelectedEventData
  | ThreadViewedEventData
  | ThreadInspectorClickEventData
  | ContextStudioThreadOpenedEventData
  | ContextStudioFilterChangedEventData
  | ContextStudioViewedEventData
  | ContextStudioSuggestionUiActionEventData
  | ExecutionProfilerOpenedEventData
  | ExecutionProfilerTraceIdCopiedEventData
  | AgentModelEffortSelectedEventData
  | UserContextExhaustPromptDecisionEventData
  | UserContextExhaustPromptViewedEventData;

const _eventTypeToData = {
  [ClientAnalyticsEventType.FEATURE_GATE_CLICKED]: FeatureGateClickedEventData,
  [ClientAnalyticsEventType.DATA_CONNECTION_CREATION_FAILED]:
    DataConnectionCreationEventData,
  [ClientAnalyticsEventType.DATA_CONNECTION_FORM_OPENED]:
    DataConnectionCreationEventData,
  [ClientAnalyticsEventType.AUTOCOMPLETE_ACCEPTED]:
    SqlAutocompleteAcceptedEventData,
  [ClientAnalyticsEventType.AUTOCOMPLETE_TRIGGERED]:
    SqlAutocompleteTriggeredEventData,
  [ClientAnalyticsEventType.PROJECT_EMPTY_STATE_CELL_CLICKED]:
    ProjectEmptyStateCellClickedEventData,
  [ClientAnalyticsEventType.FEATURE_FLAG_EVALUATED]:
    FeatureFlagEvaluatedEventData,
  [ClientAnalyticsEventType.ONBOARDING_STEP_ADVANCED]:
    OnboardingStepAdvancedEventData,
  [ClientAnalyticsEventType.VIEW_DATA_OPENED]: ViewDataOpenedEventData,
  [ClientAnalyticsEventType.PROJECT_TEMPLATE_PREVIEWED]:
    ProjectTemplatePreviewedEventData,
  [ClientAnalyticsEventType.DEBUG_MODE_OPENED]: DebugModeOpenedEventData,
  [ClientAnalyticsEventType.NOTIFICATION_INBOX_OPENED]:
    NotificationInboxOpenedEventData,
  [ClientAnalyticsEventType.NOTIFICATION_CLICKED]: NotificationClickedEventData,
  [ClientAnalyticsEventType.KEYBOARD_SHORTCUT_USED]:
    KeyboardShortcutUsedEventData,
  [ClientAnalyticsEventType.PRESENTATION_MODE_OPENED]:
    PresentationModeOpenedEventData,
  [ClientAnalyticsEventType.DATA_BROWSER_DIALOG_OPENED]:
    DataBrowserDialogOpenedEventData,
  [ClientAnalyticsEventType.DATA_BROWSER_TABLE_SELECTED]:
    DataBrowserTableSelectedEventData,
  [ClientAnalyticsEventType.PUBLISH_MODAL_OPENED]: PublishModalOpenedEventData,
  [ClientAnalyticsEventType.CLIENT_COUNTER_EVENT]: ClientCounterEventData,
  [ClientAnalyticsEventType.PROJECT_LIST_LAYOUT_UPDATED]:
    ProjectListLayoutUpdatedEventData,
  [ClientAnalyticsEventType.PROJECT_LIST_SORT_UPDATED]:
    ProjectListSortUpdatedEventData,
  [ClientAnalyticsEventType.PROJECT_LIST_FILTER_UPDATED]:
    ProjectListFilterUpdatedEventData,
  [ClientAnalyticsEventType.EXPLORE_CREATED]: ExploreCreatedEventData,
  [ClientAnalyticsEventType.EXPLORE_ERRORED]: ExploreErroredEventData,
  [ClientAnalyticsEventType.SEMANTIC_DATASET_SELECTED]:
    SemanticDatasetSelectedEventData,
  [ClientAnalyticsEventType.CTA_CLICKED]: CtaClickedEventData,
  [ClientAnalyticsEventType.FEATURE_GATE_CONTACT_SALES_SUBMITTED]:
    FeatureGateContactSalesSubmittedEventData,
  [ClientAnalyticsEventType.CTA_CONTACT_SALES_SUBMITTED]:
    CtaContactSalesSubmittedEventData,
  [ClientAnalyticsEventType.CANCELLATION_STEP_ADVANCED]:
    CancellationStepAdvancedEventData,
  [ClientAnalyticsEventType.SEMANTIC_WORKBENCH_PUBLISH_TAB_CLICKED]:
    SemanticWorkbenchPublishTabClickedEventData,
  [ClientAnalyticsEventType.DRILLED_INTO_EXPLORE]: DrilledIntoExploreEventData,
  [ClientAnalyticsEventType.SEMANTIC_VIEW_SELECTED]:
    SemanticViewSelectedEventData,
  [ClientAnalyticsEventType.THREAD_VIEWED]: ThreadViewedEventData,
  [ClientAnalyticsEventType.THREAD_INSPECTOR_CLICK]:
    ThreadInspectorClickEventData,
  [ClientAnalyticsEventType.DATA_BROWSER_SEARCH_RESULT_SELECTED]:
    DataBrowserSearchResultSelectedEventData,
  [ClientAnalyticsEventType.DATA_BROWSER_SEARCH_TRIGGERED]:
    DataBrowserSearchTriggeredEventData,
  [ClientAnalyticsEventType.WORKSPACE_SEARCH_RESULT_CLICKED]:
    WorkspaceSearchResultClickedEventData,
  [ClientAnalyticsEventType.CONTEXT_STUDIO_THREAD_OPENED]:
    ContextStudioThreadOpenedEventData,
  [ClientAnalyticsEventType.CONTEXT_STUDIO_FILTER_CHANGED]:
    ContextStudioFilterChangedEventData,
  [ClientAnalyticsEventType.CONTEXT_STUDIO_VIEWED]:
    ContextStudioViewedEventData,
  [ClientAnalyticsEventType.CONTEXT_STUDIO_SUGGESTION_UI_ACTION]:
    ContextStudioSuggestionUiActionEventData,
  [ClientAnalyticsEventType.EXECUTION_PROFILER_OPENED]:
    ExecutionProfilerOpenedEventData,
  [ClientAnalyticsEventType.EXECUTION_PROFILER_TRACE_ID_COPIED]:
    ExecutionProfilerTraceIdCopiedEventData,
  [ClientAnalyticsEventType.AGENT_MODEL_EFFORT_SELECTED]:
    AgentModelEffortSelectedEventData,
  [ClientAnalyticsEventType.USER_CONTEXT_EXHAUST_PROMPT_DECISION]:
    UserContextExhaustPromptDecisionEventData,
  [ClientAnalyticsEventType.USER_CONTEXT_EXHAUST_PROMPT_VIEWED]:
    UserContextExhaustPromptViewedEventData,
} as const satisfies Record<ClientAnalyticsEventType, Runtype>;

type BatchClientAnalyticsEventMap = {
  [K in keyof typeof _eventTypeToData]: {
    event: K;
    properties: Requiredish<Static<(typeof _eventTypeToData)[K]>>;
  };
};
export type BatchClientAnalyticsEvent =
  BatchClientAnalyticsEventMap[keyof BatchClientAnalyticsEventMap];

type Distribute<T> = T extends { event: string; properties: any }
  ? [T["event"], T["properties"]]
  : never;
export type BatchClientAnalyticsTuples = Distribute<BatchClientAnalyticsEvent>;

/**
 * Browser-only page context sent from the client to the server's
 * `/track-page` endpoint. The server forwards these to Rudderstack's `page`
 * call, so the Rudderstack client SDK no longer needs to run in the browser.
 *
 * These mirror the standard page properties the Rudderstack JS SDK used to
 * auto-populate. All fields are strings (empty string when absent) so the
 * payload shape is stable regardless of the current location. The
 * `initial_*` fields capture the referrer of the first page in the session and
 * are persisted client-side, since that state previously lived in the SDK.
 */
export const ClientAnalyticsPageData = z.object({
  path: z.string(),
  url: z.string(),
  tab_url: z.string(),
  search: z.string(),
  title: z.string(),
  referrer: z.string(),
  referring_domain: z.string(),
  initial_referrer: z.string(),
  initial_referring_domain: z.string(),
});
export type ClientAnalyticsPageData = z.infer<typeof ClientAnalyticsPageData>;
